import React from 'react';
import { useApolloClient } from '@apollo/client';

import { SubscriptionEntitlement } from '~/api/graphql';
import { useEntitlements } from '~/components/entitlements';
import { useNavigationAccess } from '~/components/navigation';
import { queryTerms } from './match';
import { visibleSearchSections } from './sections';
import { SECTION_RESULT_LIMIT, type SearchAccess, type SearchSectionResults } from './types';

/**
 * Keystroke-to-fan-out delay. Long enough that typing a word does not launch a
 * round of queries per letter, short enough to feel like live filtering.
 */
const DEBOUNCE_MS = 140;

export const useSearchAccess = (): SearchAccess => {
  const navigation = useNavigationAccess();
  const { hasEntitlement } = useEntitlements();
  const proPresets = hasEntitlement(SubscriptionEntitlement.CopilotProPresets);

  return React.useMemo(() => ({ ...navigation, proPresets }), [navigation, proPresets]);
};

export type UniversalSearchState = {
  /** Resolved sections that matched, in registry order. Empty ones are absent. */
  groups: SearchSectionResults[];
  /** Sections still in flight, so the palette can hold a placeholder for them. */
  pendingKeys: string[];
  resolvedCount: number;
  totalCount: number;
  loading: boolean;
  /** True only once every section has answered and none of them matched. */
  empty: boolean;
};

/**
 * Fans a query out across every section the viewer can see, one query each,
 * and reports them as they land rather than at the end. A section that throws
 * is dropped: one failing list must not blank the whole palette.
 */
export const useUniversalSearch = (query: string): UniversalSearchState => {
  const client = useApolloClient();
  const access = useSearchAccess();
  const sections = React.useMemo(() => visibleSearchSections(access), [access]);

  const [debounced, setDebounced] = React.useState(query);
  const [resolved, setResolved] = React.useState<Record<string, SearchSectionResults>>({});
  const [pending, setPending] = React.useState<string[]>([]);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  React.useEffect(() => {
    const trimmed = debounced.trim();
    if (!trimmed) {
      setResolved({});
      setPending([]);
      return;
    }

    // Guards against an earlier, slower fan-out overwriting a later one's
    // results: only the run this effect started may still publish.
    let current = true;
    const context = {
      query: trimmed,
      terms: queryTerms(trimmed),
      client,
      access
    };

    setResolved({});
    setPending(sections.map((section) => section.key));

    sections.forEach((section) => {
      Promise.resolve()
        .then(() => section.search(context))
        .then((results) => {
          if (!current || results.length === 0) {
            return;
          }
          setResolved((previous) => ({
            ...previous,
            [section.key]: {
              key: section.key,
              title: section.title,
              icon: section.icon,
              results: results.slice(0, SECTION_RESULT_LIMIT)
            }
          }));
        })
        .catch(() => {
          // Swallowed deliberately: a section the viewer cannot reach, or a
          // list that happens to be down, drops out of the results instead of
          // taking the palette with it.
        })
        .finally(() => {
          if (current) {
            setPending((previous) => previous.filter((key) => key !== section.key));
          }
        });
    });

    return () => {
      current = false;
    };
  }, [debounced, client, access, sections]);

  return React.useMemo(() => {
    const groups = sections
      .map((section) => resolved[section.key])
      .filter((group): group is SearchSectionResults => !!group);

    const total = sections.length;
    const loading = pending.length > 0;

    return {
      groups,
      pendingKeys: pending,
      resolvedCount: total - pending.length,
      totalCount: total,
      loading,
      empty: !loading && debounced.trim().length > 0 && groups.length === 0
    };
  }, [sections, resolved, pending, debounced]);
};
